import { useState } from "react";
import { toast } from "react-toastify";
import { LuTrash2 } from "react-icons/lu";
import { useDropzone } from "react-dropzone";
import { MdOutlineFileUpload } from "react-icons/md";
import Papa from "papaparse";

import { Button } from "~/components/v4/themed/Button";
import { Input } from "~/components/v3/themed/Input";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { useInvites } from "../contexts/InvitesContext";

type Invite = { name: string | null; email: string };

export const PreapproveGuests = () => {
    const [inviteEmail, setInviteEmail] = useState("");
    const [skippedEntries, setSkippedEntries] = useState<string[]>([]);
    const { preapproved, setPreapproved } = useInvites();

    const { getRootProps, getInputProps } = useDropzone({
        accept: "text/csv" as any,
        onDrop: (acceptedFiles) => {
            toast.loading("Parsing CSV...", {
                toastId: "csv-parsing",
            });
            acceptedFiles.forEach((file) => {
                Papa.parse(file, {
                    skipEmptyLines: true,
                    complete: (_result) => {
                        let result = _result as any;
                        if (result.errors.length) {
                            console.error(
                                "Errors while parsing:",
                                result.errors,
                            );
                            return;
                        }

                        let parsedInvites: Invite[] = [];
                        let skipped: string[] = [];

                        // Check if the first row looks like a header
                        const hasHeader = result.data[0].some(
                            (cell: string) =>
                                cell.toLowerCase().includes("name") ||
                                cell.toLowerCase().includes("email"),
                        );

                        const dataToProcess = hasHeader
                            ? result.data.slice(1)
                            : result.data;

                        dataToProcess.forEach((row: any) => {
                            let name: string | null = null;
                            let email: string | null = null;

                            if (Array.isArray(row)) {
                                [name, email] = row;
                            } else {
                                name = row.name || row.Name || null;
                                email =
                                    row.email ||
                                    row.Email ||
                                    row[
                                        Object.keys(row).find((key) =>
                                            key.toLowerCase().includes("email"),
                                        ) || ""
                                    ];
                            }

                            name = name ? name.trim() : null;
                            email = email ? email.trim() : null;

                            if (email) {
                                parsedInvites.push({ name, email });
                            } else {
                                skipped.push(`Row with name: ${name || "N/A"}`);
                            }
                        });

                        setPreapproved((prevInvites: Invite[]) => [
                            ...prevInvites,
                            ...parsedInvites,
                        ]);
                        setSkippedEntries((prevSkipped: string[]) => [
                            ...prevSkipped,
                            ...skipped,
                        ]);
                        toast.dismiss("csv-parsing");
                        toast.success("CSV parsing successful!", {
                            autoClose: 1000,
                        });
                    },
                });
            });
        },
    });

    const handleAdd = () => {
        setPreapproved([...preapproved, { name: null, email: inviteEmail }]);
        setInviteEmail("");
    };

    const handleRemove = (email: string) => {
        setPreapproved(preapproved.filter((i) => i.email !== email));
    };

    return (
        <div>
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <span className="font-[500] text-white">
                            Pre-approved guests
                        </span>

                        {preapproved.length === 0 && (
                            <div className="text-sm text-gray-500">
                                Manage pre-approval list for attendees. The
                                emails/users will get automatically approved if
                                they RSVP to the event.
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-4">
                        <Input
                            id="invite-email"
                            type="email"
                            value={inviteEmail}
                            className="w-full rounded-[10px]"
                            placeholder="m@example.com"
                            onChange={(e) => setInviteEmail(e.target.value)}
                        />
                        <Button className="rounded-full" onClick={handleAdd}>
                            Add Email
                        </Button>
                    </div>
                </div>

                {preapproved.length > 0 ? (
                    <div className="space-y-4">
                        <div className="max-h-[500px] space-y-2 overflow-y-auto">
                            {preapproved.map((user) => (
                                <div
                                    key={user.email}
                                    className="flex items-center justify-between rounded-lg p-3"
                                >
                                    <div className="flex items-center gap-2">
                                        <UserAvatar user={user} />

                                        <div className="flex flex-col">
                                            {user.name && (
                                                <div className="text-sm text-white">
                                                    {user.name}
                                                </div>
                                            )}
                                            <div className="text-sm text-gray-500">
                                                {user.email}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemove(user.email)}
                                        className="text-v3-N-300 hover:text-white"
                                    >
                                        <LuTrash2 className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            {...getRootProps()}
                            className="cursor-pointer rounded-[10px] bg-v3-N-500 p-2"
                        >
                            <input {...getInputProps()} />
                            <div className="flex min-w-[30rem] items-center gap-3">
                                <div className="rounded-full bg-v3-N-400 p-1">
                                    <MdOutlineFileUpload size={18} />
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    <span className="text-sm">
                                        Drop CSV files here or
                                    </span>
                                    <span className="text-sm text-secondary underline">
                                        select a file to upload
                                    </span>
                                </div>
                            </div>
                        </div>

                        <p className="mt-2 text-sm text-gray-500">
                            CSV format: You can include headers (name, email) or
                            just provide data. First column for names, second
                            column for emails.
                        </p>

                        <div className="flex flex-col gap-4">
                            <span className="text-white">
                                Example CSV structure:
                            </span>

                            <table className="w-full text-sm">
                                <thead>
                                    <tr>
                                        <th className="w-1/2 rounded-tl-xl border border-v3-N-600 bg-v3-N-500 px-4 py-2 text-left font-normal text-v3-N-300">
                                            Name
                                        </th>
                                        <th className="w-1/2 rounded-tr-xl border border-v3-N-600 bg-v3-N-500 px-4 py-2 text-left font-normal text-v3-N-300">
                                            Email
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="border border-v3-N-600 bg-v3-N-500 px-4 py-2 text-sm">
                                            John Doe
                                        </td>
                                        <td className="border border-v3-N-600 bg-v3-N-500 px-4 py-2">
                                            john@example.com
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="rounded-bl-xl border border-v3-N-600 bg-v3-N-500 px-4 py-2">
                                            Jane Smith
                                        </td>
                                        <td className="rounded-br-xl border border-v3-N-600 bg-v3-N-500 px-4 py-2">
                                            jane@example.com
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
